import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { object, string, boolean } from 'yup';
import { Formik } from 'formik';
import {
  Header,
  H1,
  H3,
  H4,
  Markdown,
  BlankLink,
  NoTranslate,
  Row,
  HighlightGroup,
  Icon
} from '@contently/toolkit';
import ContentRequestForm from './form';
import { post, get } from '../../utils/api';
import { handleError, handleSubmitError } from '../../utils/errors';
import GrapeFormat from '../../utils/formatters/formik-grape-formatter';
import { useCookieJar } from '../../utils/cookie_jar';

const NewContentRequest = props => {
  const { appData } = props;
  const { contentRequestFormShareToken } = JSON.parse(appData);
  const [contentRequestForm, setContentRequestForm] = useState({ title: '', brandProfile: {} });
  const [storedExternalUser, setStoredExternalUser, removeStoredExternalUser] = useCookieJar(
    'externalUser'
  );
  const externalUser = storedExternalUser || { firstName: '', lastName: '', email: '' };
  const [isFetching, setIsFetching] = useState(true);
  const [values, setValues] = useState({
    terms: false,
    projectTitle: '',
    description: '',
    dueDate: null,
    remember: !!externalUser.email,
    externalUser,
    questions: []
  });

  function setRememberExternalUser({ remember, externalUser: user }) {
    if (remember) {
      setStoredExternalUser({ ...user });
    } else {
      removeStoredExternalUser();
    }
  }

  function getContentRequestForm(token) {
    get(`/api/v1/external/content_request_forms/${token}`)
      .then(data => {
        setIsFetching(false);
        setContentRequestForm(data);
        setValues({
          ...values,
          questions: data.questions
        });
      })
      .catch(errorResponse => {
        handleError(errorResponse, () => handleError(errorResponse));
      });
  }

  function hasAttachedFile(contentRequestForm) {
    return contentRequestForm.file && contentRequestForm.file.isAttached;
  }

  useEffect(() => {
    getContentRequestForm(contentRequestFormShareToken);
  }, [contentRequestFormShareToken]);

  return (
    <div className="section-wrapper">
      <Header>
        <H1>
          <NoTranslate text={contentRequestForm.name} />
        </H1>
        <H3>
          <NoTranslate text={contentRequestForm.brandProfile.name} />
        </H3>
      </Header>

      {(contentRequestForm.instructions || hasAttachedFile(contentRequestForm)) && (
        <Row>
          <HighlightGroup>
            {contentRequestForm.instructions && (
              <Row>
                <H4>Instructions</H4>
                <NoTranslate>
                  <Markdown>{contentRequestForm.instructions}</Markdown>
                </NoTranslate>
              </Row>
            )}

            {hasAttachedFile(contentRequestForm) && (
              <Row>
                <H4>Additional materials</H4>
                <Icon type="file-text" withText />
                <BlankLink title="Additional materials" href={contentRequestForm.file.url}>
                  {contentRequestForm.file.name}
                </BlankLink>
              </Row>
            )}
          </HighlightGroup>
        </Row>
      )}
      <Row>
        <Formik
          enableReinitialize
          initialValues={{ ...values }}
          validationSchema={object().shape({
            terms: boolean().oneOf([true], 'Terms must be accepted'),
            projectTitle: string().required('Title is required'),
            externalUser: object().shape({
              firstName: string().required('First name is required'),
              lastName: string().required('Last name is required'),
              email: string()
                .required('Email is required')
                .email('Must be a valid email')
            })
          })}
          onSubmit={(formValues, actions) => {
            setRememberExternalUser(formValues);

            post(
              '/api/v1/external/content_requests',
              JSON.stringify(
                GrapeFormat(
                  {
                    ...formValues,
                    contentRequestFormId: contentRequestForm.id
                  },
                  ['questions']
                )
              )
            )
              .then(contentRequest => {
                window.location.replace(
                  `/ext/content_requests/${contentRequest.data.attributes.shareToken}`
                );
              })
              .catch(errorResponse => {
                handleSubmitError(
                  errorResponse,
                  actions,
                  'There was an error saving your content request form.'
                );
              });
          }}
          render={innerProps => (
            <ContentRequestForm
              {...innerProps}
              contentRequestForm={contentRequestForm}
              isFetching={isFetching}
            />
          )}
        />
      </Row>
    </div>
  );
};

NewContentRequest.propTypes = {
  appData: PropTypes.string.isRequired
};

export default NewContentRequest;



// WEBPACK FOOTER //
// ./app/javascript/app/components/content_requests/new_content_request.jsx